import React, { useCallback, useState, memo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { Button } from "@material-ui/core";
import {
  Container,
  Grid,
  TextField,
  PrimaryButtonContained,
} from "../components";
import { login, loginSSO } from "../actions/user";
import { resetTimer } from "../actions/timer";
import { notify } from "../actions/notification";
import { MicrosoftSSO } from "@mb/react-components";
// Same artwork as <MicrosoftSSO /> (outlined Microsoft sign-in button)
import MicrosoftLogo from "@mb/react-components/lib/esm/assets/images/microsoft_logo.png";
import {
  getAzureSamlLoginUrl,
  getSamlProfileFromLoginPageUrl,
  isSamlLoginRoute,
  shouldShowOidcLoginButton,
  shouldShowSamlLoginButton,
} from "../utils/azureSamlSso";
import { persistSamlAuthMethod } from "../utils/azureAdLogout";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: 100,
  },
  logoContainer: {
    justifyContent: "center",
    display: "flex",
    width: "100%",
    "& img": {
      width: "300px",
      marginTop: "2rem",
    },
  },
  loginSSOBtn: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    marginTop: "15px",
  },
  msSamlIcon: {
    height: "1.5em",
    marginRight: theme.spacing(1),
  },
}));

function Login() {
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation();

  useEffect(() => {
    if (isSamlLoginRoute()) {
      persistSamlAuthMethod(getSamlProfileFromLoginPageUrl());
    }
  }, []);

  const startAzureSamlSso = useCallback(() => {
    try {
      persistSamlAuthMethod(getSamlProfileFromLoginPageUrl());
      window.location.assign(getAzureSamlLoginUrl());
    } catch (err) {
      dispatch(
        notify({
          type: "error",
          message: err.message || t("Your login attempt failed, please try again later."),
        })
      );
    }
  }, [dispatch, t]);

  const onLogin = useCallback(
    (e) => {
      if (e && e.preventDefault) {
        e.preventDefault();
      }
      dispatch(resetTimer());
      dispatch(login(loginData));
    },
    [loginData, dispatch]
  );

  const onInputChange = useCallback(
    (ev) => {
      const { name, value } = ev.target;
      setLoginData({
        ...loginData,
        [name]: value,
      });
    },
    [loginData, setLoginData]
  );

  const validateToken = (info) => {
    const { accessToken, idToken, idTokenClaims } = info;
    dispatch(loginSSO({ accessToken, idToken, groups: idTokenClaims.groups }));
  };

  return (
    <Container maxWidth="xs" className={classes.root}>
      <form onSubmit={onLogin}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              name="email"
              type="email"
              label={t("Email")}
              value={loginData.email}
              onChange={onInputChange}
              fullWidth
              data-test-id="login-email-input-field"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="password"
              type="password"
              label={t("Password")}
              value={loginData.password}
              onChange={onInputChange}
              fullWidth
              data-test-id="login-password-input-field"
            />
          </Grid>
          <Grid item xs={12}>
            <PrimaryButtonContained
              type="submit"
              onClick={onLogin}
              fullWidth
              data-test-id="login-button"
            >
              {t("Log In")}
            </PrimaryButtonContained>
            {shouldShowOidcLoginButton() ? (
              <div className={classes.loginSSOBtn}>
                <MicrosoftSSO
                  clientId={process.env.REACT_APP_MICROSOFT_SSO_CLIENT_KEY}
                  onAuthenticate={(session) => validateToken(session)}
                  redirectUri={window.location.origin + "/login"}
                />
              </div>
            ) : null}
            {shouldShowSamlLoginButton() ? (
              <div className={classes.loginSSOBtn}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={startAzureSamlSso}
                  data-test-id="login-azure-saml-button"
                  aria-label="Login With Microsoft (SAML)"
                >
                  <img
                    src={MicrosoftLogo}
                    alt=""
                    className={classes.msSamlIcon}
                    aria-hidden
                  />
                  Login With Microsoft (SAML)
                </Button>
              </div>
            ) : null}
            <div className={classes.logoContainer}>
              <img alt="logo" src="/assets/logo.png" />
            </div>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
}
export default memo(Login);
